// client/src/components/public/PublicHubPage.jsx
//
// /hub/:tenantSlug — the tenant's public hub ("link in bio"): live now,
// upcoming and (optionally) past public events, open raffles + auctions
// (optional; full list at /hub/:tenantSlug/actions, PublicActionsPage.jsx),
// custom links, hub forms (Phase 4), socials. No auth, no app chrome. Growth Phase 8, base package.
// Staff edit it at /:tenant/public-hub (hub/HubSettingsPage.jsx). Crawler
// meta for this URL is injected by the server (routes/publicPageRoutes.js).

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchHubForms, fetchPublicHub } from '../../services/hubApi';
import { assetUrl } from '../../utils/assetUrl';
import PublicFormRenderer from '../forms/PublicFormRenderer';
import GoalProgressBar from '../donations/GoalProgressBar';
import PublicActionCard from './PublicActionCard';
import '../forms/Forms.css';
import './PublicHub.css';

const SOCIAL_LABELS = {
  instagram: 'Instagram', facebook: 'Facebook', x: 'X', tiktok: 'TikTok', youtube: 'YouTube',
  linkedin: 'LinkedIn', threads: 'Threads', bluesky: 'Bluesky', pinterest: 'Pinterest',
};

/** Month / day / time of an ISO instant in the tenant's time zone. */
function dateParts(iso, timeZone) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return null;
  const tz = timeZone || undefined;
  try {
    return {
      month: d.toLocaleString(undefined, { month: 'short', timeZone: tz }),
      day: d.toLocaleString(undefined, { day: 'numeric', timeZone: tz }),
      when: d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz }),
    };
  } catch {
    return dateParts(iso, undefined);
  }
}

function EventCard({ ev, timeZone, live }) {
  const { t } = useTranslation();
  const p = dateParts(ev.startsAt, timeZone);
  return (
    <a className="hub-event" href={ev.url}>
      {p && (
        <span className="hub-date" aria-hidden="true">
          <span className="m">{p.month}</span>
          <span className="d">{p.day}</span>
        </span>
      )}
      <span className="hub-event-body">
        <span className="hub-event-name">
          {ev.name}
          {live && <span className="hub-live">{t('hub.public.liveBadge', 'Live now')}</span>}
        </span>
        <span className="hub-event-meta">{[p?.when, ev.venueName].filter(Boolean).join(' · ')}</span>
        {ev.headline && <span className="hub-event-meta">{ev.headline}</span>}
      </span>
    </a>
  );
}

export default function PublicHubPage() {
  const { t } = useTranslation();
  const { tenantSlug } = useParams();
  const [data, setData] = useState(null);
  const [forms, setForms] = useState([]);
  const [state, setState] = useState('loading');

  // A public page must not wear the app theme (see PublicEventPage).
  useEffect(() => {
    const html = document.documentElement;
    const removed = [...html.classList].filter((c) => c.startsWith('theme-'));
    removed.forEach((c) => html.classList.remove(c));
    return () => removed.forEach((c) => html.classList.add(c));
  }, []);

  useEffect(() => {
    let alive = true;
    setState('loading');
    fetchPublicHub(tenantSlug)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setState('ready');
        document.title = d.hub?.seoTitle || d.tenant?.name || document.title;
      })
      .catch((err) => { if (alive) setState(err.status === 404 ? 'not-found' : 'error'); });
    return () => { alive = false; };
  }, [tenantSlug]);

  // Hub forms (Phase 4). Optional: any failure just shows none.
  const showForms = data?.hub?.sections?.forms;
  useEffect(() => {
    if (!showForms) return undefined;
    let alive = true;
    fetchHubForms(tenantSlug)
      .then((res) => { if (alive) setForms(res.data || []); })
      .catch(() => { if (alive) setForms([]); });
    return () => { alive = false; };
  }, [tenantSlug, showForms]);

  if (state !== 'ready') {
    const msg = {
      loading: t('hub.public.loading', 'Loading…'),
      'not-found': t('hub.public.notFound', "This page isn't available."),
      error: t('hub.public.loadError', 'Something went wrong loading this page.'),
    }[state];
    return (
      <div className="hub-page">
        <div className="hub-inner"><p className="hub-status" role={state === 'error' ? 'alert' : undefined}>{msg}</p></div>
      </div>
    );
  }

  const { tenant, hub, events, calendar } = data;
  const tz = tenant.timezone;
  const socials = Object.entries(hub.socials || {});
  const style = { '--hub-accent': hub.theme.accent, '--hub-accent-text': hub.theme.accentText };

  return (
    <div className={`hub-page${hub.theme.mode === 'dark' ? ' dark' : ''}`} style={style}>
      <div className="hub-inner">
        <header className="hub-head">
          {tenant.logo && <img src={assetUrl(tenant.logo)} alt="" />}
          <h1>{tenant.name}</h1>
          {tenant.description && <p>{tenant.description}</p>}
          {tenant.website && (
            <p><a href={tenant.website} target="_blank" rel="noopener noreferrer">{tenant.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a></p>
          )}
        </header>

        {(data.donations || []).length > 0 && (
          <section className="hub-section" aria-labelledby="hub-give">
            <h2 id="hub-give">{t('hub.public.give', 'Support us')}</h2>
            {data.donations.map((g) => (
              <div key={g.slug} className="dn-card" style={{ marginBottom: 12 }}>
                <GoalProgressBar title={g.title} raisedCents={g.raisedCents} goalCents={g.goalCents} donors={g.donors} currency={g.currency} href={g.url} />
              </div>
            ))}
          </section>
        )}

        {/* Open raffles + auctions: a preview, the rest at /hub/:slug/actions. Hidden when none are open. */}
        {data.actions?.total > 0 && (
          <section className="hub-section" aria-labelledby="hub-actions">
            <h2 id="hub-actions">{t('hub.public.actions', 'Raffles & auctions')}</h2>
            {data.actions.items.map((a) => <PublicActionCard key={a.id} action={a} timeZone={tz} />)}
            <Link className="hub-link hub-link-quiet" to={`/hub/${encodeURIComponent(tenant.slug)}/actions`}>
              {t('hub.public.actionsAll', { count: data.actions.total, defaultValue: 'See all {{count}} raffles & auctions' })}
            </Link>
          </section>
        )}

        {events.live.length > 0 && (
          <section className="hub-section" aria-labelledby="hub-live">
            <h2 id="hub-live">{t('hub.public.live', 'Happening now')}</h2>
            {events.live.map((ev) => <EventCard key={ev.id} ev={ev} timeZone={tz} live />)}
          </section>
        )}

        {hub.sections.upcoming && (
          <section className="hub-section" aria-labelledby="hub-upcoming">
            <h2 id="hub-upcoming">{t('hub.public.upcoming', 'Upcoming events')}</h2>
            {events.upcoming.length
              ? events.upcoming.map((ev) => <EventCard key={ev.id} ev={ev} timeZone={tz} />)
              : <p className="hub-empty">{t('hub.public.noUpcoming', 'Nothing scheduled right now. Check back soon.')}</p>}
          </section>
        )}

        {hub.customLinks.length > 0 && (
          <section className="hub-section" aria-label={t('hub.public.links', 'Links')}>
            {hub.customLinks.map((l, i) => (
              <a key={`${l.url}-${i}`} className="hub-link" href={l.url} target="_blank" rel="noopener noreferrer">{l.label}</a>
            ))}
          </section>
        )}

        {forms.length > 0 && (
          <section className="hub-section">
            {forms.map((f) => <PublicFormRenderer key={f.id} form={f} tenantSlug={tenantSlug} />)}
          </section>
        )}

        {socials.length > 0 && (
          <section className="hub-section" aria-labelledby="hub-socials">
            <h2 id="hub-socials">{t('hub.public.follow', 'Follow')}</h2>
            <div className="hub-socials">
              {socials.map(([k, url]) => (
                <a key={k} className="hub-social" href={url} target="_blank" rel="noopener noreferrer me">{SOCIAL_LABELS[k] || k}</a>
              ))}
            </div>
          </section>
        )}

        {events.past.length > 0 && (
          <section className="hub-section" aria-labelledby="hub-past">
            <h2 id="hub-past">{t('hub.public.past', 'Past events')}</h2>
            {events.past.map((ev) => <EventCard key={ev.id} ev={ev} timeZone={tz} />)}
          </section>
        )}

        <p className="hub-foot">
          {calendar?.feed && (
            <>
              <a href={calendar.feed.replace(/^https?:/, 'webcal:')}>{t('hub.public.subscribe', 'Subscribe to this calendar')}</a>
              {' · '}
            </>
          )}
          {t('hub.public.poweredBy', 'Powered by On-Screen')}
        </p>
      </div>
    </div>
  );
}
